import React, { useState, useRef, useEffect } from 'react';
import { useCurrency } from '../contexts/CurrencyContext';

const CurrencySelector = () => {
  const { currency, setCurrency } = useCurrency();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCurrencyChange = (newCurrency) => {
    setCurrency(newCurrency);
    setIsOpen(false);
  };

  // Currency icon URLs
  const CAD_ICON = '/images/cad-icon.png';
  const USD_ICON = '/images/usd-icon.png';
  
  const currentIcon = currency === 'CAD' ? CAD_ICON : USD_ICON;

  return (
    <div 
      ref={dropdownRef}
      className="fixed bottom-6 right-6 z-50"
      style={{ zIndex: 9999 }}
    >
      {/* Floating Currency Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="bg-white hover:bg-gray-50 text-slate-700 font-semibold py-3 px-4 rounded-full shadow-lg transition-all duration-200 hover:shadow-xl flex items-center space-x-2 relative"
        style={{
          border: '2px solid transparent',
          backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #0007b5 0%, #4a7cff 100%)',
          backgroundOrigin: 'border-box',
          backgroundClip: 'padding-box, border-box'
        }}
        aria-label="Select Currency"
      >
        <img src={currentIcon} alt={currency} className="w-5 h-5 object-contain" />
        <span className="text-sm font-bold">$</span>
        <svg 
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute bottom-full mb-2 right-0 bg-white rounded-lg shadow-xl border-2 border-slate-200 overflow-hidden min-w-[160px]">
          <div className="py-1">
            <button
              onClick={() => handleCurrencyChange('CAD')}
              className={`w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors flex items-center justify-between ${
                currency === 'CAD' ? 'bg-blue-100 text-blue-700 font-semibold' : 'text-slate-700'
              }`}
            >
              <span className="flex items-center space-x-2">
                <img src={CAD_ICON} alt="CAD" className="w-5 h-5 object-contain" />
                <span>$CAD</span>
              </span>
              {currency === 'CAD' && (
                <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </button>
            <button
              onClick={() => handleCurrencyChange('USD')}
              className={`w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors flex items-center justify-between ${
                currency === 'USD' ? 'bg-blue-100 text-blue-700 font-semibold' : 'text-slate-700'
              }`}
            >
              <span className="flex items-center space-x-2">
                <img src={USD_ICON} alt="USD" className="w-5 h-5 object-contain" />
                <span>$USD</span>
              </span>
              {currency === 'USD' && (
                <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </button>
          </div>
          <div className="border-t border-slate-200 px-4 py-2 bg-slate-50">
            <p className="text-xs text-slate-500 text-center">
              1 CAD ≈ 0.73 USD
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CurrencySelector;
